import classnames from 'classnames';
import { pick, pickBy } from 'lodash';
import * as PropTypes from 'prop-types';
import React, { Component } from 'react';
import localeRu from 'date-fns/locale/ru';
import format from 'date-fns/format';
import addYears from 'date-fns/addYears';
import DatePicker, { registerLocale } from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';
import { toRequiredLabel } from 'utils';
import HelpTooltip from 'components/Tooltip/HelpTooltip';
import CustomDatePickerInput from 'components/DateField/CustomDatePickerInput';
import styles from 'components/DateField/DateField.module.scss';
import withMomentAdapter from 'components/DateField/withMomentAdapter';
import DateFieldRange from 'components/DateField/DateRange';
import DateFormats from 'constants/DateFormats';

registerLocale('ru', localeRu);

class DateField extends Component {
  static getDerivedStateFromProps(nextProps, prevState) {
    if (
      nextProps.startDateValue !== prevState.startDateValue ||
      nextProps.endDateValue !== prevState.endDateValue
    ) {
      return {
        startDateValue: DateField.checkValidDateProps(nextProps.startDateValue),
        endDateValue: DateField.checkValidDateProps(nextProps.endDateValue),
      };
    }
    return null;
  }

  static checkValidDateProps(date) {
    if (date === 'Invalid date') {
      return null;
    }
    return isValidDate(date) ? date : null;
  }

  state = {
    startDateValue: DateField.checkValidDateProps(this.props.startDateValue),
    endDateValue: DateField.checkValidDateProps(this.props.endDateValue),
  };

  dateToString = (date) => {
    const dateString = date instanceof Date ? format(date, DateFormats.short) : date;
    return dateString || '';
  };

  dateTimeToString = (date) => {
    const dateString = date instanceof Date ? format(date, DateFormats.short_with_time) : date;
    return dateString || '';
  };

  handleChangeStart = (date) => {
    this.setState({ startDateValue: date });
    const { handleChangeStart } = this.props;
    if (handleChangeStart) {
      handleChangeStart(date);
    }
  };

  handleChangeEnd = (date) => {
    this.setState({ endDateValue: date });
    const { handleChangeEnd } = this.props;
    if (handleChangeEnd) {
      handleChangeEnd(date);
    }
  };

  onBlurValue = (value) => {
    this.props.onBlur(value);
  };

  getExtendedProperties = () => {
    const fields = [
      'inline',
      'showMonthDropdown',
      'showYearDropdown',
      'minDate',
      'maxDate',
      'excludeDates',
      'includeDates',
      'forceShowMonthNavigation',
      'peekNextMonth',
      'monthsShown',
      'placeholderText',
      'dateFormat',
      'yearDropdownItemNumber',
      'locale',
    ];
    return pick(this.props, fields);
  };

  validate() {
    const { isValid, validationMessage } = this.props;
    let message = null;

    if (validationMessage) {
      message = (
        <div className={classnames('form_error', { green: isValid, red: !isValid })}>
          {validationMessage}
        </div>
      );
    }

    return message;
  }

  renderTooltip = () => {
    const { tooltipContent } = this.props;
    return tooltipContent ? (
      <HelpTooltip content={tooltipContent} title={this.props.tooltipTitle} />
    ) : null;
  };

  renderSingleDate() {
    const {
      isDisabled,
      label,
      isRequired,
      id,
      children,
      position,
      isValid,
      validationMessage,
      classNameWrapper,
      hasHelpWrap,
      showDateTime,
    } = this.props;
    const { startDateValue } = this.state;
    const extendedProps = this.getExtendedProperties();
    const cleanedProps = pickBy(extendedProps, (v) => v !== undefined && v !== null);
    const className = classnames(
      'form_field',
      styles.dateField,
      showDateTime && styles.dateTimeField,
      {
        form_field__disable: !!isDisabled,
        form_field__error: !isValid && validationMessage,
      },
    );
    const props = {
      selected: startDateValue,
      onChange: this.handleChangeStart,
      value: showDateTime
        ? this.dateTimeToString(startDateValue)
        : this.dateToString(startDateValue),
      disabled: isDisabled,
      onBlur: () => this.onBlurValue(startDateValue),
      className,
      ...cleanedProps,
    };

    return (
      <div id={id}>
        {label && <div className="form_label">{isRequired ? toRequiredLabel(label) : label}</div>}
        {hasHelpWrap ? (
          <div
            className={classnames([
              'help-wrap',
              { [`help-wrap__${position}`]: position },
              classNameWrapper,
            ])}
          >
            <DatePicker
              key={`key-${id}`}
              {...props}
              autoComplete={'off'}
              allowSameDay={true}
              customInput={
                <CustomDatePickerInput mask={showDateTime ? '11.11.1111 11:11' : '11.11.1111'} />
              }
            />
            <div key="lkfl-form-input-children">{children}</div>
            {this.renderTooltip()}
          </div>
        ) : (
          <DatePicker
            key={`key-${id}`}
            {...props}
            customInput={
              <CustomDatePickerInput mask={showDateTime ? '11.11.1111 11:11' : '11.11.1111'} />
            }
          />
        )}
      </div>
    );
  }

  renderRangeDate() {
    const {
      isDisabled,
      label,
      tooltipContent,
      isRequired,
      id,
      isValid,
      validationMessage,
    } = this.props;
    const { startDateValue, endDateValue } = this.state;
    const extendedProps = this.getExtendedProperties();
    const className = classnames('form_field', {
      [styles.dateField]: !isDisabled,
      form_field__disable: !!isDisabled,
      form_field__error: !isValid && validationMessage,
    });
    return (
      <div id={id} className="form-date ">
        {label && (
          <div className="form-date_subtitle">{isRequired ? toRequiredLabel(label) : label}</div>
        )}
        <div className={`position-relative ${tooltipContent && styles.dateFieldTooltip}`}>
          <DateFieldRange
            startDateValue={startDateValue}
            endDateValue={endDateValue}
            dateToString={this.dateToString}
            handleChangeStart={this.handleChangeStart}
            handleChangeEnd={this.handleChangeEnd}
            onBlur={this.onBlurValue}
            className={className}
            {...extendedProps}
          />
          {this.renderTooltip()}
        </div>
      </div>
    );
  }

  render() {
    const { endDateValue, style, className } = this.props;
    return (
      <div className={`form_item form_item__part ${styles.datePicker} ${className}`} style={style}>
        {endDateValue !== undefined ? this.renderRangeDate() : this.renderSingleDate()}
        {this.validate()}
      </div>
    );
  }
}

function isValidDate(d) {
  return d instanceof Date && !isNaN(d);
}

export default withMomentAdapter(DateField);
DateField.propTypes = {
  className: PropTypes.string,
  handleChangeStart: PropTypes.func.isRequired,
  handleChangeEnd: PropTypes.func,
  onBlur: PropTypes.func,
  isDisabled: PropTypes.bool,
  label: PropTypes.string,
  startDateValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  endDateValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isValid: PropTypes.bool,
  validationMessage: PropTypes.string,
  isRequired: PropTypes.bool,
  inline: PropTypes.bool,
  showMonthDropdown: PropTypes.bool,
  showYearDropdown: PropTypes.bool,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  excludeDates: PropTypes.array,
  includeDates: PropTypes.array,
  forceShowMonthNavigation: PropTypes.bool,
  peekNextMonth: PropTypes.bool,
  monthsShown: PropTypes.number,
  placeholderText: PropTypes.string,
  id: PropTypes.string,
  style: PropTypes.object,
  position: PropTypes.string,
  tooltipContent: PropTypes.node,
  tooltipTitle: PropTypes.string,
  classNameWrapper: PropTypes.string,
  children: PropTypes.node,
  hasHelpWrap: PropTypes.bool,
  dateFormat: PropTypes.string,
  yearDropdownItemNumber: PropTypes.number,
  locale: PropTypes.string,
  showDateTime: PropTypes.bool,
};

DateField.defaultProps = {
  isDisabled: false,
  width: null,
  message: null,
  onBlur: () => false,
  inline: false,
  showMonthDropdown: true,
  showYearDropdown: true,
  minDate: new Date('01-01-1900'),
  maxDate: addYears(new Date(), 50),
  excludeDates: null,
  includeDates: null,
  forceShowMonthNavigation: null,
  peekNextMonth: true,
  monthsShown: 1,
  position: 'inline',
  hasHelpWrap: true,
  dateFormat: 'dd.MM.yyyy',
  yearDropdownItemNumber: 200,
  locale: 'ru',
  showDateTime: false,
};
